import moment from 'moment';
import apiEndpoint from '../../../config/data';
import { showToast } from '@spotoninc/nexus-react';
import { getAuthHeader } from '../../../../utils/tokenService';
// Heap Analytics tracking functions
// Note: These functions handle Heap availability checks internally via heapAnalytics.js
// No need to check Heap availability at this level - the underlying trackEvent function
// handles all availability checks, error handling, and graceful degradation
import {
  trackTaskCreated,
  trackTaskEdited,
  trackTaskCommented,
} from '../../../../utils/analytics/taskTracking';

/**
 * Task Form Service
 * Contains CRUD operations specifically for task form operations
 */
class TaskFormService {
  /**
   * Get authorization headers for API calls
   */
  getAuthHeaders() {
    const authHeader = getAuthHeader();
    return authHeader ? { Authorization: authHeader } : {};
  }

  /**
   * Add a new task
   * @param {Object} taskData - Task data object
   * @param {string} taskData.taskName - Name of the task
   * @param {string} taskData.comment - Comments of the task
   * @param {string} taskData.ownerId - ID of the user assigned to the task
   * @param {string} taskData.dueDate - Due date of the task
   * @param {string} taskData.priority - Priority of the task (High, Medium, Low)
   */
  async addTask({
    taskName,
    ownerId,
    dueDate,
    priority = 'Medium',
    status,
    comment,
  }) {
    try {
      const taskData = new FormData();
      taskData.append('version', '2');
      taskData.append('task_title', taskName);
      taskData.append('comment', comment);
      taskData.append('assigned_to', ownerId);
      taskData.append('status', status);
      taskData.append(
        'due_on',
        moment(dueDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
      );

      const response = await fetch(apiEndpoint + '/api/v2/modify_task/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: taskData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Track task creation in Heap
      trackTaskCreated({
        taskId: result.data?.toString() || 'unknown',
        taskTitle: taskName,
        taskStatus: status,
        assignedTo: ownerId,
      });

      showToast({
        priority: 'success',
        content: 'Task added successfully',
        autoClose: 6000,
      });
    } catch (error) {
      console.error('Error adding task:', error);

      showToast({
        priority: 'error',
        content: 'Failed to add task: ' + (error.message || 'Unknown error'),
        autoClose: 6000,
      });

      throw error;
    }
  }

  /**
   * Modify an existing task
   * @param {Object} taskData - Task data object
   * @param {string|number} taskData.taskId - ID of the task to modify
   * @param {string} taskData.taskName - Name of the task
   * @param {string} taskData.comment - Comments of the task
   * @param {string} taskData.ownerId - ID of the user assigned to the task
   * @param {string} taskData.dueDate - Due date of the task
   * @param {string} taskData.status - Status of the task
   * @param {string} taskData.priority - Priority of the task (High, Medium, Low)
   */
  async modifyTask({
    version,
    taskId,
    taskName,
    ownerId,
    dueDate,
    status,
    comment,
    priority,
  }) {
    try {
      const taskData = new FormData();
      taskData.append('version', version);
      taskData.append('task_title', taskName);
      taskData.append('assigned_to', ownerId);
      taskData.append('status', status);
      taskData.append(
        'due_on',
        moment(dueDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
      );
      taskData.append('comment', comment);
      taskData.append('taskId', taskId);
      taskData.append('action', 'modify');

      const response = await fetch(apiEndpoint + '/api/v2/modify_task/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: taskData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();

      // Track task edit in Heap
      trackTaskEdited({
        taskId: taskId,
        taskTitle: taskName,
        taskStatus: status,
      });

      // Track comment if a new comment was added (non-empty)
      if (comment && comment.trim() !== '') {
        trackTaskCommented({
          taskId: taskId,
          taskTitle: taskName,
          comment: comment,
        });
      }

      showToast({
        priority: 'success',
        content: 'Task updated successfully',
        autoClose: 6000,
      });
    } catch (error) {
      console.error('Error modifying task:', error);

      showToast({
        priority: 'error',
        content: 'Failed to update task: ' + (error.message || 'Unknown error'),
        autoClose: 6000,
      });

      throw error;
    }
  }

  /**
   * Fetch all users from the API
   */
  async fetchUsers() {
    try {
      const response = await fetch(apiEndpoint + '/api/get_users/', {
        method: 'GET',
        credentials: 'include',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const dd_data = Array.isArray(data['data'])
        ? data['data'].map(item => ({
            label: item.first_name,
            value: item.user_id_id,
          }))
        : [];

      return dd_data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Update priority in task description
   * @param {string} taskDesc - Original task description
   * @param {string} priority - New priority level
   * @returns {string} Updated task description with priority
   */
  updatePriorityInDescription(taskDesc, priority) {
    // Check if the description already has a Priority section
    if (taskDesc.includes('Priority:')) {
      // Replace the existing priority
      return taskDesc.replace(
        /Priority:\s*(High|Medium|Low)/i,
        `Priority: ${priority}`
      );
    } else {
      // Add a new Priority section at the end of the description
      return taskDesc.trim() + `\n\nPriority: ${priority}`;
    }
  }
}

export const taskFormService = new TaskFormService();
export default taskFormService;
